<!-- 服务主页 -->
<template>
  <div class="dialogs" v-if="showDialog">
    <div class="dialog-text">
      {{ $t('international.international_btn_title') }}
    </div>
    <div class="dialog-button">
      <button class="btn" @click="toInternational">
        {{ $t('international.international_btn_cancel') }}
      </button>
      <button class="btn btn-right" @click="sure">
        {{ $t('international.international_btn_confirm') }}
      </button>
    </div>
  </div>
  <LazyLayoutsBanner>
    <template #content>
      <div class="input-wrapper">
        <ClientOnly>
          <el-input
            v-model="searchInput"
            :maxlength="50"
            :placeholder="$t('search.pc_entry_placeholder')"
            @keyup.enter.native="changeFn"
            @focus="focusFn"
            @blur="blurFn">
            <template #prefix>
              <svg-icon name="icon-search" class="s-icon" />
            </template>
          </el-input>
        </ClientOnly>
        <el-button
          type="primary"
          v-if="isShowSubmit"
          round
          :disabled="isEmpty(searchInput.trim())"
          color="#3475ff"
          @click="searchFn">
          {{ $t('search.search_placeholder') }}
        </el-button>
        <SearchHistory
          v-if="isShowHistory"
          :historyArr="historyArr"
          @searchHistory="searchHistory" />
      </div>
    </template>
  </LazyLayoutsBanner>
  <!-- 产品支持  -->
  <div class="product-con" v-if="categoryTreesFlag && categoryTrees.length > 0">
    <div class="title">{{ $t('support.product_support') }}</div>
    <div class="sub-title">{{ $t('support.product_support_desc') }}</div>
    <div class="tab-box">
      <el-tabs v-model="curIdx" class="tabs" @tab-click="handleTabChange">
        <el-tab-pane
          v-for="(item, index) in categoryTrees"
          :label="item.name"
          :name="index" />
      </el-tabs>
      <div class="more-box" v-if="showCategoryTreesMore">
        <svg-icon name="ic_arrow_right" class="svg" @click="goToProduct" />
        <div class="tooltips">{{ $t('support.more') }}</div>
      </div>
    </div>
    <div
      class="swiper-box"
      :class="{ 'less-items': categoryTrees[curIdx]?.children.length < 5 }">
      <Swiper
        :modules="[SwiperNavigation]"
        :slides-per-view="5"
        :effect="'creative'"
        :pagination="{
          type: 'fraction'
        }"
        :navigation="true"
        class="my-swiper">
        <SwiperSlide
          v-for="childItem in categoryTrees[curIdx]?.children"
          :key="childItem.code">
          <div class="items" @click="goToSubSeries(childItem)">
            <div class="items-img-con">
              <el-image
                :src="childItem.lightUrl"
                :alt="childItem.name"
                class="child-img"
                loading="lazy"
                fit="contain" />
            </div>
            <div class="child-desc max-line2">
              {{ childItem.name }}
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
    <div class="product-see-more" @click="seeMore">
      {{ $t('support.see_more') }}
    </div>
  </div>
  <!--热门服务 -->
  <div class="hot-service" v-if="hotServerList.length > 0">
    <div class="title server-title">{{ $t('support.hot_server') }}</div>
    <BaseCardList
      :options="hotServerList"
      @cardClick="data => serviceCardClick(data, $t('support.hot_server'))" />
  </div>
  <!-- 广告位 -->
  <div class="activities" v-if="activitiesList.length > 0">
    <div class="title">{{ $t('support.ad_space') }}</div>
    <div class="activities-con">
      <Swiper
        :modules="[SwiperPagination, SwiperAutoplay, SwiperNavigation]"
        :autoplay="{
          delay: 5000,
          disableOnInteraction: false
        }"
        :loop="true"
        :slide-per-view="1"
        :pagination="{
          clickable: true,
          renderBullet
        }"
        :navigation="true"
        @slide-change="onSlideChange"
        :effect="'creative'"
        class="activities-swiper">
        <SwiperSlide v-for="item in activitiesList">
          <img
            :src="item.lightImgUrl"
            class="activities-img"
            @click="goToLink(item)" />
        </SwiperSlide>
      </Swiper>
    </div>
  </div>

  <!-- 更多服务 -->
  <div class="more-service" v-if="moreServerList.length > 0">
    <div class="title server-title">{{ $t('support.more_services') }}</div>
    <BaseCardList
      :options="moreServerList"
      @cardClick="
        data => serviceCardClick(data, $t('support.more_services'))
      " />
  </div>
  <!-- 在线客服 -->
  <!-- 俄罗斯没有客服 -->
  <LazySupportOnlineService v-if="environmentData.country !== 'ru'" />
  <!-- 商店 -->
  <!-- 亚太通用没有商城 -->
  <LazySupportStore v-if="environmentData.country !== 'ap'" />
</template>
<script setup lang="ts">
import {
  getProductAndSeries,
  getAdActivities,
  getIndexConfigList
} from '@support/api';
import {
  ProductSelectType,
  getAsyncData,
  OpenType,
  isEmpty,
  openInNewTabWithParams,
  ENV_MODE,
  BusinessType,
  logEvent,
  environmentData
} from '@support/utils';
import { useLocalData, useUrl } from '@support/json';
import { showMessage } from '~/utils/index';

import { useI18n } from 'vue-i18n';

const { $sensorTrack } = useNuxtApp();
const { t } = useI18n();

useHead({
  title: t('seo.seo_title'),
  meta: [
    {
      name: 'description',
      content: t('seo.seo_desc')
    },
    {
      name: 'keywords',
      content: t('seo.seo_keywords')
    }
  ]
});

const router = useRouter();
const categoryTrees = ref<Array<any>>([]);
const categoryTreesFlag = ref(false);
const hotServerList = ref<Array<any>>([]); // 热门服务列表
const moreServerList = ref<Array<any>>([]); // 更多服务列表

const showCategoryTreesMore = ref(false);
const curIdx = ref(0);
const { hotServer, homeMoreServices, adConfig } = useLocalData();

let showDialog = ref(false);

getIndexConfigData();
getProductAndSeriesData();
getAdActivitiesData();
onBeforeMount(() => {
  $sensorTrack('self_service_exposure');
  const accordValue = JSON.parse(localStorage.getItem('accord_value_pc'));
  showDialog.value = accordValue ? accordValue.show : false;
  if (showDialog.value) {
    $sensorTrack('continue_watch_exposure');
    const maskEle = document.getElementsByClassName('mask')[0];
    maskEle.style.display = 'block';
  }
});

const swiperNavClick = (des: any) => {};
async function getProductAndSeriesData() {
  try {
    const { data } = await useAsyncData(() =>
      getProductAndSeries({ business: BusinessType.serviceSupport })
    );
    const trees = getAsyncData(data).data;
    showCategoryTreesMore.value = trees.length > 5;
    categoryTrees.value = trees.slice(0, 5);

    setTimeout(() => {
      categoryTreesFlag.value = true;
    });
  } catch (error: any) {
    error?.msg && ElMessage.error(error.msg);
  }
}

// 获取热门服务是否显示， 更多服务是否显示
async function getIndexConfigData() {
  try {
    await nextTick();
    const { data } = await useAsyncData(() => getIndexConfigList());
    let businessTypeData = getAsyncData(data).data;
    if (businessTypeData?.businessType == null) {
      businessTypeData = {
        businessType: []
      };
    }
    if (businessTypeData?.businessType == undefined) {
      logEvent({
        errorMsg: '官网主页-businessType属性不存在',
        businessDesc: '官网主页-销区配置'
      });
    }
    // 热门服务
    const judgeShowHotList = hotServer.filter(
      (item: any) =>
        businessTypeData?.businessType?.includes(item.businessType) ||
        item.businessType == null
    );
    hotServerList.value = judgeShowHotList;

    // 更多服务
    const judgeShowMoreList = homeMoreServices.filter(
      (item: any) =>
        businessTypeData?.businessType?.includes(item.businessType) ||
        item.businessType == null
    );

    moreServerList.value = judgeShowMoreList;
  } catch (error: any) {
    error?.msg && showMessage(error.msg, 'error');
  }
}

function serviceCardClick(item: any, moduleType: string) {
  $sensorTrack('self_service_click', {
    click_type: t(item.name),
    module_type: moduleType
  });
  switch (Number(item.openType)) {
    case OpenType.instructions:
      openInNewTabWithParams({
        path: '/product/modelSelect',
        query: {
          goType: ProductSelectType.instructions
        }
      });
      break;
    case OpenType.policyDetail:
      openInNewTabWithParams({
        path: '/product/seriesSelect',
        query: {
          goType: ProductSelectType.policyDetail
        }
      });
      break;
    case OpenType.selfInspectManual:
      openInNewTabWithParams({
        path: '/product/seriesSelect',
        query: {
          goType: ProductSelectType.selfInspectManual
        }
      });
      break;
    case OpenType.selfRepair:
      openInNewTabWithParams({
        path: '/product/modelSelect',
        query: {
          goType: ProductSelectType.selfRepair
        }
      });
      break;
    case OpenType.innerUrl:
      openInNewTabWithParams({ path: item.openUrl });
      break;
    case OpenType.link:
      useWindowsOpen(item.openUrl);
      break;
    case OpenType.backHome:
      router.push({
        path: '/'
      });
      break;
  }
}
function goToProduct() {
  $sensorTrack('self_service_click', {
    click_type: t('support.see_more'),
    module_type: t('support.product_support')
  });
  openInNewTabWithParams({
    path: '/product/modelSelect',
    query: {
      goType: ProductSelectType.vehicleSupport
    }
  });
}
// 产品选择页
function goToSubSeries(data: any) {
  const subCategoryData = categoryTrees.value[curIdx.value];
  const {
    name: subCategoryName,
    lightUrl: subCategoryIcon,
    code: subCategoryCode
  } = subCategoryData;

  $sensorTrack('self_service_click', {
    vehicle_series_id: data.code,
    vehicle_category_id: subCategoryCode,
    module_type: t('support.product_support')
  });

  openInNewTabWithParams({
    path: '/product/modelSelect',
    query: {
      seriesCode: data.code,
      seriesName: data.name,
      subCategoryCode,
      subCategoryName,
      // subCategoryIcon,
      goType: ProductSelectType.vehicleSupport
    }
  });
}
// 车辆选择页
function seeMore() {
  const data = categoryTrees.value[curIdx.value];
  // const firstChild = categoryTrees.value[curIdx.value].children[0];
  $sensorTrack('self_service_click', {
    click_type: t('support.see_more'),
    module_type: t('support.product_support')
  });
  openInNewTabWithParams({
    path: '/product/modelSelect',
    query: {
      subCategoryCode: data.code,
      subCategoryName: data.name,
      // seriesCode: firstChild.code,
      // seriesName: firstChild.name,
      // subCategoryIcon: data.lightUrl,
      goType: ProductSelectType.vehicleSupport
    }
  });
}
function handleTabChange(pane: any, ev: Event) {
  $sensorTrack('self_service_click', {
    vehicle_category_id: categoryTrees.value[curIdx.value].code,
    module_type: t('support.product_support')
  });
}

// 搜索
const searchInput = ref('');
const isShowSubmit = ref(false);
const historyArr = ref<String[]>([]);
const isShowHistory = ref(false);

const searchCom = () => {
  if (searchInput.value.trim() === '') return;
  isShowHistory.value = false;
  const trimValue = searchInput.value.trim();
  const findIndex = historyArr.value.indexOf(trimValue);
  if (findIndex > -1) {
    historyArr.value.splice(findIndex, 1);
  }
  historyArr.value.unshift(trimValue);
  localStorage.setItem('search-history', JSON.stringify(historyArr.value));
  openInNewTabWithParams({
    path: '/search',
    query: {
      keyword: trimValue
    }
  });
};
const changeFn = () => {
  searchCom();
};
const searchFn = () => {
  searchCom();
};

const focusFn = () => {
  // 展示历史记录
  const historyList = localStorage.getItem('search-history');
  // console.log('historyArr', historyArr.value);

  if (!historyList) {
    isShowHistory.value = false;
  } else {
    const hObj = [...new Set(JSON.parse(historyList))];
    if (hObj.length === 0) {
      isShowHistory.value = false;
    } else {
      isShowHistory.value = true;
      historyArr.value = hObj.length > 5 ? hObj.slice(0, 5) : hObj;
    }
  }
  // 展示提交按钮
  isShowSubmit.value = true;
  console.log('获取焦点了 focus');
};

const blurFn = () => {
  setTimeout(() => {
    isShowHistory.value = false;
    isShowSubmit.value = false;
  }, 300);
};

const searchHistory = (htyItem: any) => {
  isShowHistory.value = false;
  searchInput.value = htyItem;
  const findIndex = historyArr.value.indexOf(searchInput.value);
  if (findIndex > -1) {
    historyArr.value.splice(findIndex, 1);
  }
  historyArr.value.unshift(htyItem);
  localStorage.setItem('search-history', JSON.stringify(historyArr.value));
  openInNewTabWithParams({
    path: '/search',
    query: {
      keyword: htyItem
    }
  });
};

//  广告位
const activitiesList = ref<any>([]);
function renderBullet(index: number, className: string) {
  return `<span class="${className}"></span>`;
}
async function getAdActivitiesData() {
  try {
    const recommendId = adConfig[ENV_MODE].pcId;

    const { data } = await useAsyncData(() =>
      getAdActivities({
        recommendId
      })
    );
    activitiesList.value = getAsyncData(data).data;
  } catch (error: any) {
    error?.msg && ElMessage.error(error.msg);
  }
}
function goToLink(data: any) {
  const recommendId = adConfig[ENV_MODE].pcId;
  $sensorTrack('service_page_ad_click', {
    ad_location_id: recommendId,
    ad_id: data.id,
    ad_name: data.name
  });
  if (data?.redirectUrl) {
    useWindowsOpen(data?.redirectUrl);
  }
}
function onSlideChange() {
  // $sensorTrack('switch_ads_click');
}
const sure = () => {
  $sensorTrack('continue_watch_click', {
    continue_watch_yesorno: 1
  });
  showDialog.value = false;
  const maskEle = document.getElementsByClassName('mask')[0];
  maskEle.style.display = 'none';
};
const toInternational = () => {
  $sensorTrack('continue_watch_click', {
    continue_watch_yesorno: 0
  });
  showDialog.value = false;
  const maskEle = document.getElementsByClassName('mask')[0];
  maskEle.style.display = 'none';
  router.push({
    path: '/international'
  });
};

onUnmounted(() => {
  $sensorTrack('self_service_leave');
});
</script>
<style lang="scss" scoped>
.title {
  height: 40px;
  text-align: center;
  font-size: 32px;
  font-weight: 600;
  color: #000000;
  line-height: 40px;
}
.hot-service {
  width: 1200px;
  margin: 0 auto 0;
  margin-top: 40px;
  // padding: 40px 300px 0;
}
.server-title {
  margin-bottom: 30px;
}
.product-con {
  // height: 623px;
  width: 1200px;
  margin: 0 auto 0;
  margin-top: 40px;
  background: #ffffff;
  .sub-title {
    margin-top: 10px;
    font-weight: 500;
    font-size: 16px;
    color: #000000;
    line-height: 24px;
    opacity: 0.5;
    text-align: center;
  }
  .tab-box {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    .more-box {
      margin-left: 80px;
      display: flex;
      .tooltips {
        font-size: 12px;
        line-height: 24px;
        color: #000000;
        opacity: 0.5;
        text-align: center;
        visibility: hidden;
      }
    }
    .more-box:hover {
      .tooltips {
        visibility: visible;
      }
    }

    .svg {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      cursor: pointer;
    }
    .tabs {
      flex-shrink: 1;
      :deep(.el-tabs__nav-wrap::after) {
        display: none;
      }
      :deep(.el-tabs__item.is-active) {
        color: #000;
      }
      :deep(.el-tabs__item) {
        max-width: 300px;
        height: 32px;
        color: #00000099;
        font-weight: 500;
        font-size: 16px;
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        &:hover {
          color: #000;
        }
      }
      :deep(.el-tabs__active-bar) {
        background-color: #157be1;
      }
      :deep(.el-tabs__header) {
        margin: 0;
      }
    }
  }

  .swiper-box {
    // border: 1px solid red;
    margin-top: 20px;
    width: 1200px;
    .items {
      width: 200px;
      text-align: center;
      cursor: pointer;
      .items-img-con {
        width: 200px;
        height: 200px;
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        background: #f3f6fa;
        .child-img {
          width: 200px;
          height: 176px;
        }
      }
      .child-desc {
        // height: 50px;
        width: 200px;
        font-size: 16px;
        font-weight: 500;
        opacity: 0.5;
        color: #000000;
      }
    }
    .items:hover {
      .child-desc {
        opacity: 1;
        color: #157be1;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
  .product-see-more {
    height: 24px;
    margin-top: 27px;
    text-align: center;
    font-size: 16px;
    opacity: 0.5;
    color: #000000;
    // font-family: SF Pro Display-Medium, SF Pro Display;
    font-weight: 500;
  }
  .product-see-more:hover {
    opacity: 1;
    color: #157be1;
    text-decoration: underline;
    cursor: pointer;
  }
}
.activities {
  width: 1200px;
  margin: auto;
  margin-top: 40px;
  .activities-con {
    width: 1200px;
    margin-top: 30px;
    .activities-img {
      width: 1200px;
      height: 360px;
      cursor: pointer;
      border-radius: 10px;
    }
  }
}

.more-service {
  width: 1200px;
  margin: 40px auto;
}
.s-icon {
  width: 18px;
  height: 18px;
}
.input-wrapper {
  position: absolute;
  top: 63%;
  left: 50%;
  transform: translate(
    -50%,
    0
  ); // 本来应该上下居中，但是UAT提的输入框挡住嘴巴了，所以现在上下是不居中的
  width: 620px;
  height: 48px;
}
.el-input {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}
:deep(.el-input__wrapper) {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50px;
  &.is-focus {
    box-shadow: none;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
}

:deep(.el-input__inner) {
  font-size: 16px !important;
  padding-right: 70px;
  box-sizing: border-box;
  &::-webkit-input-placeholder {
    color: #666;
  }
}
.el-button {
  // width: 64px;
  height: 32px;
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translate(0, -50%);
  font-size: 16px;
  line-height: 32px;
  font-weight: normal;
  padding: 8px 15px; // 这个padding是 button 本身有的， 设置下可以缩放，否则缩小不成功
}
.dialogs {
  width: 300px;
  min-height: 160px;
  z-index: 700;
  position: absolute;
  top: 8px;
  left: 360px;
  background: #ffffff;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  border-radius: 10px 10px 10px 10px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  .dialog-text {
    font-weight: 500;
    font-size: 18px;
    color: #000000;
    opacity: 0.5;
    padding: 16px 16px 20px 16px;
    word-break: break-all;
  }
  .dialog-button {
    padding: 0 16px 16px;
    font-size: 18px;
    .btn {
      width: 130px;
      height: 40px;
      border-radius: 5px 5px 5px 5px;
      border: 1px solid #1d2226;
      background: #ffffff;
    }
    .btn:focus {
      outline: none;
      box-shadow: none;
    }
    .btn-right {
      color: #ffffff;
      background: #1d2226;
      margin-left: 8px;
    }
  }
}
</style>
<style lang="scss">
.less-items {
  .swiper-wrapper {
    // gap: 20px;
    justify-content: center;
    .swiper-slide {
      margin-right: 20px;
    }
  }
}
.activities-con {
  .swiper {
    position: relative;
  }
  .swiper-button-next,
  .swiper-button-prev {
    background-size: cover; /* 背景图片覆盖整个元素 */
    background-position: center; /* 背景图片居中 */
    background-repeat: no-repeat;
    width: 40px;
    height: 40px;
  }

  .swiper-button-next {
    right: 10px;
    background-image: url('@/assets/images/icon-banner-right.png');
  }

  .swiper-button-prev {
    left: 10px;
    background-image: url('@/assets/images/icon-banner-left.png');
  }
  .swiper-button-prev:after,
  .swiper-button-next:after {
    content: none;
  }
}
.swiper-box {
  .swiper {
    padding: 0 40px 0 60px;
    .swiper-wrapper .swiper-slide {
      margin-right: 20px;
      // gap: 20px;
    }
  }
  .swiper-button-prev,
  .swiper-button-next {
    width: 40px;
    height: 200px;
    top: 0;
    margin-top: 0;
    background: #f3f6faff;
    color: #000000ff;
  }

  .swiper-button-next {
    right: 0;
  }

  .swiper-button-prev {
    left: 0;
  }

  .swiper-button-prev:after,
  .swiper-button-next:after {
    font-size: 32px;
  }
  .swiper-slide {
    width: 200px !important;
  }
}
.activities-swiper {
  .swiper-pagination-bullet {
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background: #fff;
    opacity: 0.3;
    text-align: center;
  }

  .swiper-pagination-bullet-active {
    // background: #000000;
    opacity: 1;
  }
}
</style>
